import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  alpha,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useMediaQuery,
  Menu,
  ListItemIcon,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  QueryStats as QueryStatsIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptLongIcon,
  PictureAsPdf,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingActionsIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import EscritorioSelect from '../../components/selects/EscritorioSelect';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import api from '../../services/useApi';
import Swal from 'sweetalert2';
import { formatarCPFCNPJ } from '../../utils/formatToView';
import EditNfcomForm from '../../components/forms/EditNfcomForm';
import NfcomViewInfo from '../../components/modals/NfcomViewInfo';
import invoiceService from '../../services/invoiceService';
import { relatorioPdfService } from '../../services/relatorioPdfService';
import { useRelatorioCores } from '../relatorios';

interface NotaNfcom {
  id: number;
  pdf?: string;
  numero: number;
  serie: number;
  chave_acesso?: string;
  protocolo?: string;
  status: 'autorizado' | 'rejeitado' | 'cancelado' | 'pendente_transmissao';
  valor_total: number;
  fatura_id: number;
  cliente_nome: string;
  cliente_id: number;
  cliente_cpf: string;
  motivo_status?: string;
  gateway_response?: any;
  data_emissao?: string;
  createdAt: string;
  updatedAt: string;
  escritorio_id?: number; // opcional, se presente será usado no filtro
}

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}>
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{titulo}</Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>{valor}</Typography>
          {subtitulo && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{subtitulo}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const NotasNfcomPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState<null | HTMLElement>(null);
  const relatorioCores = useRelatorioCores();
  const primario = theme.palette.primary.main;
  const [notas, setNotas] = useState<NotaNfcom[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [openEditForm, setOpenEditForm] = useState(false);
  const [editingNotaId, setEditingNotaId] = useState<number | null>(null);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [viewingNotaId, setViewingNotaId] = useState<number | null>(null);
  const [selectedNotas, setSelectedNotas] = useState<number[]>([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [transmittingSelecionadas, setTransmittingSelecionadas] = useState(false);
  const formatDate = (d: Date) => {
    try {
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };
  const getFirstDayOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };
  const getLastDayOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [filtroEscritorioId, setFiltroEscritorioId] = useState<number | null>(null);

  useEffect(() => {
    setDataInicio(formatDate(getFirstDayOfCurrentMonth()));
    setDataFim(formatDate(getLastDayOfCurrentMonth()));
  }, []);

  const handleDataRangeChange = (range: { startDate: Date | null; endDate: Date | null }) => {
    setDataInicio(range.startDate ? formatDate(range.startDate) : '');
    setDataFim(range.endDate ? formatDate(range.endDate) : '');
  };

  const dateRangeValue = useMemo(() => {
    const parseDateString = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      const [year, month, day] = dateStr.split('-').map(Number);
      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      return new Date(year, month - 1, day);
    };

    return {
      startDate: dataInicio ? parseDateString(dataInicio) : null,
      endDate: dataFim ? parseDateString(dataFim) : null,
    };
  }, [dataInicio, dataFim]);

  const notasFiltradas = useMemo(() => {
    if (!Array.isArray(notas)) return [];
    return notas
      .filter((n) => {
        const matchSearch = searchTerm === '' ||
          n.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(n.numero).includes(searchTerm) ||
          n.chave_acesso?.includes(searchTerm);

        const matchEscritorio = filtroEscritorioId === null || filtroEscritorioId === undefined ||
          Number(n.escritorio_id) === Number(filtroEscritorioId);

        const matchStatus = filtroStatus === 'todos' || n.status === filtroStatus;

        return matchSearch && matchEscritorio && matchStatus;
      })
      .sort((a, b) => (b.numero || 0) - (a.numero || 0));
  }, [notas, searchTerm, filtroEscritorioId, filtroStatus]);

  const totaisNotasFiltradas = useMemo(() => {
    const qtd = notasFiltradas.length;
    const valorTotal = notasFiltradas.reduce((acc, n) => acc + Number(n.valor_total || 0), 0);
    return { qtd, valorTotal };
  }, [notasFiltradas]);

  const notasParaKpi = useMemo(() => {
    if (!Array.isArray(notas)) return [];
    return notas.filter((n) => {
      const matchSearch = searchTerm === '' ||
        n.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(n.numero).includes(searchTerm) ||
        n.chave_acesso?.includes(searchTerm);

      const matchEscritorio = filtroEscritorioId === null || filtroEscritorioId === undefined ||
        Number(n.escritorio_id) === Number(filtroEscritorioId);

      return matchSearch && matchEscritorio;
    });
  }, [notas, searchTerm, filtroEscritorioId]);

  const contagemPorStatus = useMemo(() => ({
    autorizado: notasParaKpi.filter((n) => n.status === 'autorizado').length,
    rejeitado: notasParaKpi.filter((n) => n.status === 'rejeitado').length,
    cancelado: notasParaKpi.filter((n) => n.status === 'cancelado').length,
    pendente: notasParaKpi.filter((n) => n.status === 'pendente_transmissao').length,
  }), [notasParaKpi]);

  const statusOptions = [
    { value: 'todos', label: 'Todos' },
    { value: 'autorizado', label: 'Autorizado' },
    { value: 'rejeitado', label: 'Rejeitado' },
    { value: 'cancelado', label: 'Cancelado' },
    { value: 'pendente_transmissao', label: 'Pendente de Transmissão' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'autorizado':
        return 'success';
      case 'rejeitado':
        return 'error';
      case 'cancelado':
        return 'warning';
      case 'pendente_transmissao':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'autorizado':
        return 'Autorizado';
      case 'rejeitado':
        return 'Rejeitado';
      case 'cancelado':
        return 'Cancelado';
      case 'pendente_transmissao':
        return 'Pendente de Transmissão';
      default:
        return status;
    }
  };

  useEffect(() => {
    if (dataInicio && dataFim) {
      carregarNotas();
    }
  }, [dataInicio, dataFim]);

  const carregarNotas = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);

      const response = await api.get(`/notas-nfcom?${params.toString()}`);

      // O backend retorna { success: true, data: [...] }
      if (response.data && response.data.success) {
        setNotas(response.data.data || []);
      } else {
        setNotas([]);
      }
    } catch (error) {
      console.error('Erro ao carregar notas:', error);
      mostrarSnackbar('Erro ao carregar notas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const mostrarSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTransmitir = async (id: number) => {
    try {
      setLoading(true);
      Swal.fire({
        title: 'Aguarde',
        html: 'Transmitindo NFCom...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        customClass: {
          popup: 'swal-zindex',
          container: 'swal-zindex'
        }
      });
      const resp = await api.post(`/notas-nfcom/transmitir/${id}`);
      const r: any = resp;
      const success = r?.success ?? r?.data?.success ?? true;
      const message = r?.message ?? r?.data?.message ?? (success ? 'NFCom transmitida com sucesso' : 'Falha ao transmitir NFCom');
      Swal.close();
      await Swal.fire({
        icon: success ? 'success' : 'error',
        title: success ? 'Sucesso' : 'Erro',
        text: message,
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });
      carregarNotas();
    } catch (err: any) {
      console.error('Erro ao transmitir NFCom:', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Erro ao emitir NFCom';
      Swal.close();
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: msg,
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });
      carregarNotas();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const confirmacao = await Swal.fire({
      title: 'Confirmar exclusão?',
      text: 'Esta ação não poderá ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Não, Cancelar'
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    Swal.fire({
      title: 'Aguarde',
      text: 'Aguarde cancelando...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await api.delete(`/notas-nfcom/${id}`);
      Swal.close();
      await Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: (response && response.data && (response.data.message || response.data.msg)) || 'Nota deletada com sucesso'
      });
      carregarNotas();
    } catch (error: any) {
      console.error('Erro ao deletar nota:', error);
      Swal.close();
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || 'Erro ao deletar nota'
      });
    }
  };
  const handleCancelar = async (id: number) => {
    const confirmacao = await Swal.fire({
      title: 'Confirmar cancelamento?',
      text: 'Deseja realmente cancelar esta nota?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Voltar'
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    Swal.fire({
      title: 'Aguarde',
      text: 'Aguarde cancelando...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await api.put(`/notas-nfcom/${id}/cancelar`);
      Swal.close();
      await Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: (response && response.data && (response.data.message || response.data.msg)) || 'Nota cancelada com sucesso'
      });
      carregarNotas();
    } catch (error: any) {
      console.error('Erro ao cancelar nota:', error);
      Swal.close();
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || 'Erro ao cancelar nota'
      });
    }
  };

  const handleConsultarStatus = async (id: number) => {
    Swal.fire({
      title: 'Aguarde',
      text: 'Aguarde consultando...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await api.get(`/notas-nfcom/${id}/status`);
      Swal.close();
      await Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: (response && response.data && (response.data.message || response.data.msg)) || 'Status consultado com sucesso'
      });
      carregarNotas();
    } catch (error: any) {
      console.error('Erro ao consultar status:', error);
      Swal.close();
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || 'Erro ao consultar status'
      });
    }
  };

  const handleDownloadXml = async (id: number, numero?: string) => {
    try {
      const token = localStorage.getItem('sysup_token');
      if (!token) {
        mostrarSnackbar('Usuário não autenticado', 'error');
        return;
      }

      const baseURL = api.defaults.baseURL;
      const response = await fetch(`${baseURL}/notas-nfcom/${id}/xml`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Falha no download');
      }

      const contentDisposition = response.headers.get('content-disposition');
      let filename = `nota_${numero || id}.xml`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao fazer download do XML:', error);
      mostrarSnackbar('Erro ao fazer download do XML', 'error');
    }
  };

  const handleDownloadXml_original = async (id: number, numero?: string) => {
    try {
      const response = await api.get(`/notas-nfcom/${id}/xml`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nota_${numero || id}.xml`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao fazer download do XML:', error);
      mostrarSnackbar('Erro ao fazer download do XML', 'error');
    }
  };



  const handleImprimirDanfe = async (notaId: number) => {
    try {
      Swal.fire({
        title: 'Aguarde',
        text: 'Gerando DANFE...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });

      const token = localStorage.getItem('sysup_token');
      if (!token) {
        Swal.close();
        mostrarSnackbar('Usuário não autenticado', 'error');
        return;
      }

      const baseURL = api.defaults.baseURL;
      const response = await fetch(`${baseURL}/notas-nfcom/${notaId}/imprimir-danfe`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Falha ao gerar DANFE');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      Swal.close();
    } catch (error: any) {
      console.error('Erro ao imprimir DANFE:', error);
      Swal.close();
      mostrarSnackbar(error.message || 'Erro ao gerar DANFE', 'error');
    }
  };

  const handleImprimirFatura = async (faturaId: number) => {
    try {
      Swal.fire({
        title: 'Aguarde',
        text: 'Gerando PDF...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' }
      });

      const blob = await invoiceService.imprimirPDF([faturaId], 'fatura');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.click();

      Swal.close();
    } catch (error) {
      console.error('Erro ao imprimir PDF:', error);
      Swal.close();
      mostrarSnackbar('Erro ao gerar PDF da fatura', 'error');
    }
  };

  const handleViewCliente = (clienteId: number) => {
    navigate(`/clients/${clienteId}`);
  };

  const handleEditNfcom = (notaId: number) => {
    setEditingNotaId(notaId);
    setOpenEditForm(true);
  };

  const handleCloseEditForm = () => {
    setOpenEditForm(false);
    setEditingNotaId(null);
  };

  const handleEditFormSuccess = () => {
    carregarNotas();
  };

  const handleViewInfo = (notaId: number) => {
    setViewingNotaId(notaId);
    setOpenInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setOpenInfoModal(false);
    setViewingNotaId(null);
  };

  const handleToggleNotaSelection = (notaId: number) => {
    setSelectedNotas(prev => {
      if (prev.includes(notaId)) {
        return prev.filter(id => id !== notaId);
      } else {
        return [...prev, notaId];
      }
    });
  };

  const handleSelectAllNotas = () => {
    if (selectedNotas.length === notasFiltradas.length && notasFiltradas.length > 0) {
      setSelectedNotas([]);
    } else {
      setSelectedNotas(notasFiltradas.map(n => n.id));
    }
  };

  const handleExportPdfRelatorio = async () => {
    if (notasFiltradas.length === 0) return;
    setExportingPdf(true);
    try {
      await relatorioPdfService.exportarNfcom(
        notasFiltradas.map((n) => n.id),
        {
          dataInicio,
          dataFim,
          status: filtroStatus,
          ...(filtroEscritorioId != null ? { escritorio: String(filtroEscritorioId) } : {}),
        }
      );
    } finally {
      setExportingPdf(false);
    }
  };

  const handleDownloadXmlsZip = async () => {
    if (selectedNotas.length === 0) {
      mostrarSnackbar('Selecione pelo menos uma nota', 'warning');
      return;
    }

    try {
      setDownloadingZip(true);
      const token = localStorage.getItem('sysup_token');
      if (!token) {
        mostrarSnackbar('Usuário não autenticado', 'error');
        return;
      }

      const baseURL = api.defaults.baseURL;
      const response = await fetch(`${baseURL}/notas-nfcom/download-xmls-zip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedNotas })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Falha no download');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `xmls_nfcom_${new Date().toISOString().slice(0, 10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      mostrarSnackbar(`${selectedNotas.length} XML(s) baixado(s) com sucesso`, 'success');
      setSelectedNotas([]); // Limpar seleção após download
    } catch (error: any) {
      console.error('Erro ao baixar XMLs em ZIP:', error);
      mostrarSnackbar(error.message || 'Erro ao baixar XMLs', 'error');
    } finally {
      setDownloadingZip(false);
    }
  };

  /** Envia os ids marcados para o backend (`POST .../transmitir-selecionadas`). */
  const handleTransmitirSelecionadas = async () => {
    if (selectedNotas.length === 0) {
      await Swal.fire({
        icon: 'info',
        title: 'Nenhuma nota selecionada',
        text: 'Marque na tabela as notas que deseja transmitir.',
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
      });
      return;
    }

    const ids = [...new Set(selectedNotas)];

    const confirmacao = await Swal.fire({
      title: 'Confirmar transmissão',
      html: `
          <div style="text-align: center;">
            <p style="font-size: 16px; margin-bottom: 10px;">
              Enviar <strong>${ids.length}</strong>
              ${ids.length === 1 ? ' nota' : ' notas'} selecionada(s) para transmissão no servidor?
            </p>
            <p style="font-size: 13px; color: #666; margin-top: 8px;">
              Apenas notas <strong>Pendente de Transmissão</strong> ou <strong>Rejeitado</strong> serão transmitidas; as demais entram como ignoradas.
            </p>
            <p style="font-size: 14px; color: #666;">
              O processo pode levar alguns minutos.
            </p>
          </div>
        `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, transmitir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    setTransmittingSelecionadas(true);
    try {
      Swal.fire({
        title: 'Transmitindo',
        html: `
          <div style="text-align: center;">
            <p style="font-size: 16px; margin-bottom: 10px;">
              Processando <strong>${ids.length}</strong>
              ${ids.length === 1 ? ' nota' : ' notas'} no servidor…
            </p>
            <p style="font-size: 14px; color: #666;">
              Não feche esta página.
            </p>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
      });

      const response = await api.post('/notas-nfcom/transmitir-selecionadas', { ids });
      const resultado = response.data as {
        success?: boolean;
        message?: string;
        sucesso?: number;
        erros?: number;
        ignoradas?: number;
      };

      Swal.close();

      const sucesso = Number(resultado.sucesso ?? 0);
      const erros = Number(resultado.erros ?? 0);
      const ignoradas = Number(resultado.ignoradas ?? 0);

      const blocoIgnoradas =
        ignoradas > 0
          ? `<p style="font-size: 14px; color: #666;">Ignoradas (status não transmissível): <strong>${ignoradas}</strong></p>`
          : '';

      if (sucesso > 0 && erros === 0) {
        await Swal.fire({
          icon: 'success',
          title: 'Transmissão concluída',
          html: `
            <p><strong>${sucesso}</strong> ${sucesso === 1 ? 'nota transmitida' : 'notas transmitidas'} com sucesso.</p>
            ${blocoIgnoradas}
          `,
          confirmButtonText: 'OK',
          customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
        });
      } else if (sucesso > 0 && erros > 0) {
        await Swal.fire({
          icon: 'warning',
          title: 'Transmissão concluída com erros',
          html: `
            <p>✅ Sucesso: <strong>${sucesso}</strong></p>
            <p>❌ Erros: <strong>${erros}</strong></p>
            ${blocoIgnoradas}
            <p style="font-size: 14px; color: #666; margin-top: 12px;">Confira as notas na lista.</p>
          `,
          confirmButtonText: 'OK',
          customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
        });
      } else if (sucesso === 0 && erros > 0) {
        await Swal.fire({
          icon: 'error',
          title: 'Erro na transmissão',
          html: `
            <p>Nenhuma nota foi transmitida com sucesso.</p>
            <p style="font-size: 14px; color: #666;">${resultado.message || ''}</p>
            ${blocoIgnoradas}
          `,
          confirmButtonText: 'OK',
          customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
        });
      } else if (sucesso === 0 && erros === 0 && ignoradas > 0) {
        await Swal.fire({
          icon: 'info',
          title: 'Nenhuma transmissão',
          html: `
            <p>Todas as <strong>${ignoradas}</strong> selecionadas foram ignoradas (apenas Pendente de Transmissão ou Rejeitado são transmitidas).</p>
          `,
          confirmButtonText: 'OK',
          customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
        });
      } else {
        await Swal.fire({
          icon: 'info',
          title: 'Concluído',
          text: resultado.message || 'Nenhuma operação de transmissão foi necessária.',
          customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
        });
      }

      await carregarNotas();
      setSelectedNotas([]);
    } catch (error: any) {
      console.error('Erro ao transmitir notas selecionadas:', error);
      Swal.close();
      await Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error?.response?.data?.message || error?.message || 'Erro ao transmitir as notas selecionadas.',
        customClass: { popup: 'swal-zindex', container: 'swal-zindex' },
      });
    } finally {
      setTransmittingSelecionadas(false);
    }
  };

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#e8eaf6', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <ReceiptLongIcon sx={{ fontSize: 120, color: relatorioCores.primario }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(relatorioCores.primario, 0.15),
                color: relatorioCores.primario,
                boxShadow: `0 4px 12px ${alpha(relatorioCores.primario, 0.25)}`,
              }}
            >
              <ReceiptLongIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gestão de Notas NFCOM</Typography>
              <Typography variant="caption" color="text.secondary">Gerenciamento de notas fiscais de comunicação</Typography>
            </Box>
          </Stack>

          {isMobile ? (
            <>
              <IconButton
                onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}
                size="small"
                aria-label="Ações (exportar, transmitir, XML, atualizar)"
                aria-haspopup="true"
                aria-expanded={Boolean(headerMenuAnchor)}
                sx={{
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1.5,
                  width: 36,
                  height: 36,
                  '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                }}
              >
                <MoreVertIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <Menu
                anchorEl={headerMenuAnchor}
                open={Boolean(headerMenuAnchor)}
                onClose={() => setHeaderMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 220 } }}
              >
                <MenuItem
                  onClick={() => {
                    setHeaderMenuAnchor(null);
                    void handleExportPdfRelatorio();
                  }}
                  disabled={loading || notasFiltradas.length === 0 || exportingPdf || !dataInicio || !dataFim}
                >
                  <ListItemIcon>
                    {exportingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf fontSize="small" />}
                  </ListItemIcon>
                  {exportingPdf ? 'Exportando...' : 'Exportar PDF'}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setHeaderMenuAnchor(null);
                    void handleTransmitirSelecionadas();
                  }}
                  disabled={loading || transmittingSelecionadas || selectedNotas.length === 0}
                >
                  <ListItemIcon>
                    {transmittingSelecionadas ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
                  </ListItemIcon>
                  {transmittingSelecionadas ? 'Transmitindo…' : 'Transmitir selecionadas'}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setHeaderMenuAnchor(null);
                    navigate('/notas-nfcom/gerar-lote');
                  }}
                >
                  <ListItemIcon>
                    <PlaylistAddCheckIcon fontSize="small" />
                  </ListItemIcon>
                  Gerar lote NFCom
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setHeaderMenuAnchor(null);
                    void handleDownloadXmlsZip();
                  }}
                  disabled={loading || selectedNotas.length === 0 || downloadingZip}
                >
                  <ListItemIcon>
                    {downloadingZip ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon fontSize="small" />}
                  </ListItemIcon>
                  {downloadingZip ? 'Baixando...' : 'Baixar XML'}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setHeaderMenuAnchor(null);
                    void carregarNotas();
                  }}
                >
                  <ListItemIcon>
                    <RefreshIcon fontSize="small" />
                  </ListItemIcon>
                  Atualizar
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={exportingPdf ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdf sx={{ fontSize: 16 }} />}
                onClick={handleExportPdfRelatorio}
                disabled={loading || notasFiltradas.length === 0 || exportingPdf || !dataInicio || !dataFim}
              >
                {exportingPdf ? 'Exportando...' : 'Exportar PDF'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="success"
                startIcon={
                  transmittingSelecionadas ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SendIcon sx={{ fontSize: 16 }} />
                  )
                }
                onClick={handleTransmitirSelecionadas}
                disabled={loading || transmittingSelecionadas || selectedNotas.length === 0}
              >
                {transmittingSelecionadas ? 'Transmitindo…' : 'Transmitir selecionadas'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<PlaylistAddCheckIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/notas-nfcom/gerar-lote')}
              >
                Gerar lote NFCom
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={downloadingZip ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon sx={{ fontSize: 16 }} />}
                onClick={handleDownloadXmlsZip}
                disabled={loading || selectedNotas.length === 0 || downloadingZip}
              >
                {downloadingZip ? 'Baixando...' : 'Baixar XML'}
              </Button>
              <Tooltip title="Atualizar">
                <IconButton
                  onClick={carregarNotas}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    width: 30, height: 30,
                    '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Paper>

      {!loading && dataInicio && dataFim && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Autorizado"
              valor={contagemPorStatus.autorizado}
              subtitulo={`${notasParaKpi.length ? Math.round((contagemPorStatus.autorizado / notasParaKpi.length) * 100) : 0}% do total`}
              icone={<CheckCircleIcon sx={{ color: theme.palette.success.main }} />}
              cor={theme.palette.success.main}
              bgCor={alpha(theme.palette.success.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Rejeitado"
              valor={contagemPorStatus.rejeitado}
              icone={<ErrorOutlineIcon sx={{ color: theme.palette.error.main }} />}
              cor={theme.palette.error.main}
              bgCor={alpha(theme.palette.error.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Cancelado"
              valor={contagemPorStatus.cancelado}
              icone={<CancelIcon sx={{ color: theme.palette.warning.main }} />}
              cor={theme.palette.warning.main}
              bgCor={alpha(theme.palette.warning.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Pendente"
              valor={contagemPorStatus.pendente}
              icone={<PendingActionsIcon sx={{ color: theme.palette.info.main }} />}
              cor={theme.palette.info.main}
              bgCor={alpha(theme.palette.info.main, 0.12)}
            />
          </Grid>
        </Grid>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.5 },
          mb: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <FilterListIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <SysDateRangePicker label="Período (Emissão)" value={dateRangeValue} onChange={handleDataRangeChange} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <EscritorioSelect value={filtroEscritorioId} onChange={(val) => setFiltroEscritorioId((Array.isArray(val) ? (val[0] ?? null) : (val as number | null)))} showTodosOption size="small" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Buscar por cliente" size="small" placeholder="Buscar por cliente, número ou chave..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} fullWidth InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="notas-filtro-status-label">Status</InputLabel>
              <Select labelId="notas-filtro-status-label" label="Status" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value as string)}>
                {statusOptions.map((opt) => (<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={700}>Listagem de Notas</Typography>
          {!loading && dataInicio && dataFim && (
            <Chip size="small" label={`${notasFiltradas.length} nota${notasFiltradas.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
        </Box>

        {!dataInicio || !dataFim ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <ReceiptLongIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Selecione um período</Typography>
            <Typography variant="caption">Informe o período pela data de emissão da NFCom para listar as notas.</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando notas...</Typography>
          </Box>
        ) : notasFiltradas.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <ReceiptLongIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Nenhuma nota encontrada</Typography>
            <Typography variant="caption" color="text.secondary">Ajuste os filtros ou o período selecionado.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table
              size="small"
              sx={{
                minWidth: 800,
                '& .MuiTableBody-root .MuiTableCell-root': { py: 0.6, px: 1, fontSize: '0.78rem' },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    '& .MuiTableCell-head': {
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      py: 0.75,
                      px: 1,
                      borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={selectedNotas.length > 0 && selectedNotas.length < notasFiltradas.length}
                        checked={notasFiltradas.length > 0 && selectedNotas.length === notasFiltradas.length}
                        onChange={handleSelectAllNotas}
                      />
                    </TableCell>
                    <TableCell>Número</TableCell>
                    <TableCell>Série</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Fatura</TableCell>
                    <TableCell align="right">Valor Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notasFiltradas.map((nota) => (
                    <TableRow key={nota.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedNotas.includes(nota.id)}
                          onChange={() => handleToggleNotaSelection(nota.id)}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        {nota.numero
                          ? nota.numero.toString().padStart(9, '0').replace(/(\d{3})(\d{3})(\d{3})/, '$1.$2.$3')
                          : ''}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{nota.serie ? nota.serie.toString().padStart(3, '0') : ''}</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }} onClick={() => handleViewCliente(nota.cliente_id)} style={{ cursor: 'pointer' }}>
                        <Typography variant="body2">{nota.cliente_nome}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatarCPFCNPJ(nota.cliente_cpf)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {nota.fatura_id}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        R$ {Number(nota.valor_total || 0).toFixed(2).replace('.', ',')}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        <Chip
                          label={getStatusLabel(nota.status)}
                          color={getStatusColor(nota.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                        {nota.data_emissao
                          ? new Date(nota.data_emissao).toLocaleDateString('pt-BR')
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title="Informações">
                          <IconButton
                            size="small"
                            onClick={() => handleViewInfo(nota.id)}
                            color="info"
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        {nota.status !== 'autorizado' && nota.status !== 'cancelado' && (
                          <Tooltip title="Editar Dados NFCom">
                            <IconButton
                              size="small"
                              onClick={() => handleEditNfcom(nota.id)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(nota.status === 'autorizado' || nota.status === 'cancelado') && (
                          <Tooltip title="Download XML">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadXml(nota.id, nota.numero?.toString() || '')}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(nota.status === 'autorizado' || nota.status === 'cancelado' || nota.status === 'pendente_transmissao') && (
                          <Tooltip title="Imprimir DANFE">
                            <IconButton
                              size="small"
                              onClick={() => handleImprimirDanfe(nota.id)}
                              color="secondary"
                            >
                              <PictureAsPdf />
                            </IconButton>
                          </Tooltip>
                        )}
                        {nota.status === 'autorizado' && (
                          <Tooltip title="Imprimir Fatura">
                            <IconButton
                              size="small"
                              onClick={() => handleImprimirFatura(nota.fatura_id)}
                              color="primary"
                            >
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(nota.status === 'pendente_transmissao' || nota.status === 'rejeitado') && (
                          <Tooltip title="Transmitir">
                            <IconButton
                              size="small"
                              onClick={() => handleTransmitir(nota.id)}
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {nota.status !== 'autorizado' && nota.status !== 'cancelado' && (
                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(nota.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {nota.status === 'autorizado' && (
                          <Tooltip title="Cancelar">
                            <IconButton
                              size="small"
                              onClick={() => handleCancelar(nota.id)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {nota.status === 'autorizado' || nota.status === 'cancelado' && (
                          <Tooltip title="Consultar Status">
                            <IconButton
                              size="small"
                              onClick={() => handleConsultarStatus(nota.id)}
                            >
                              <QueryStatsIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {notasFiltradas.length > 0 && (
                    <TableRow
                      sx={{
                        bgcolor: (t) => alpha(primario, 0.05),
                        '& .MuiTableCell-root': {
                          fontWeight: 700,
                          fontSize: '0.78rem',
                        },
                      }}
                    >
                      <TableCell align="left" colSpan={5}>
                        Total: {totaisNotasFiltradas.qtd} {totaisNotasFiltradas.qtd === 1 ? 'nota' : 'notas'}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        R$ {totaisNotasFiltradas.valorTotal.toFixed(2).replace('.', ',')}
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <EditNfcomForm
        open={openEditForm}
        onClose={handleCloseEditForm}
        onSuccess={handleEditFormSuccess}
        notaId={editingNotaId}
      />

      <NfcomViewInfo
        open={openInfoModal}
        onClose={handleCloseInfoModal}
        notaId={viewingNotaId}
      />


    </Box>
  );
};

export default NotasNfcomPage;
